/**
 * FlowStageAnalysisPanel — Per-stage TP/TRA/SLA analysis for a selected workflow flow.
 * Shows a horizontal pipeline of stages with performance indicators and bottleneck detection.
 */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, Timer, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

/* ─── Types ──────────────────────────────────────────────── */

interface StageMetrics {
  stageId: string;
  stageName: string;
  stageOrder: number;
  /** Total time in stage: entry → completion (minutes) */
  avgLeadTime: number;
  /** Processing time: started → completed (minutes) */
  avgTP: number;
  /** Wait/response time: entry → started (minutes) = leadTime - TP */
  avgTRA: number;
  /** SLA compliance rate (0-100) */
  slaRate: number;
  /** Total tasks completed in this stage */
  totalTasks: number;
  /** Total SLA breaches */
  slaBreach: number;
  /** Total reprocesses */
  reprocessCount: number;
  /** Estimated TP from map/VSM (minutes) */
  tpStandard: number | null;
  /** Estimated TRA from map/VSM (minutes) */
  traStandard: number | null;
}

interface FlowStageAnalysisPanelProps {
  organizationId: string;
  templateId: string;
  flowName?: string;
}

/* ─── Data Fetching ──────────────────────────────────────── */

async function fetchStageMetrics(organizationId: string, templateId: string): Promise<StageMetrics[]> {
  // Get instances for this template
  const { data: instances, error: instErr } = await supabase
    .from('workflow_instances_v9')
    .select('id, started_at, completed_at')
    .eq('organization_id', organizationId)
    .eq('template_id', templateId)
    .limit(200);

  if (instErr) throw instErr;
  if (!instances?.length) return [];

  const instanceIds = instances.map((i) => i.id);

  // Get stage definitions with tp/tra standards (stored in metadata JSONB)
  const { data: stageDefs } = await supabase
    .from('workflow_stages_v9')
    .select('id, metadata')
    .eq('organization_id', organizationId)
    .eq('template_id', templateId);

  const stageStandards = new Map<string, { tp: number | null; tra: number | null }>();
  (stageDefs ?? []).forEach((s: any) => {
    const meta = s.metadata ?? {};
    stageStandards.set(s.id, {
      tp: typeof meta.tp_standard === 'number' ? meta.tp_standard : null,
      tra: typeof meta.tra_standard === 'number' ? meta.tra_standard : null,
    });
  });

  // Get tasks with stage info
  const { data: tasks, error: taskErr } = await supabase
    .from('workflow_tasks_v9')
    .select(`
      instance_id,
      stage_id,
      stage_name,
      status,
      started_at,
      completed_at,
      created_at,
      is_overdue,
      stage:workflow_stages_v9(stage_order, name, sla_hours)
    `)
    .eq('organization_id', organizationId)
    .in('instance_id', instanceIds);

  if (taskErr) throw taskErr;
  if (!tasks?.length) return [];

  // Get events for entry time calculation
  const { data: events } = await supabase
    .from('workflow_instance_events_v9')
    .select('instance_id, stage_id, event_type, created_at')
    .eq('organization_id', organizationId)
    .in('instance_id', instanceIds)
    .in('event_type', ['stage_advanced', 'task_started', 'task_reprocessed']);

  // Build entry lookup per stage per instance
  const stageEntryMap = new Map<string, string>(); // "instanceId:stageId" → earliest entry timestamp
  (events ?? []).forEach((ev: any) => {
    if (!ev.instance_id || !ev.stage_id || !ev.created_at) return;
    if (!['stage_advanced', 'task_started'].includes(ev.event_type)) return;
    const key = `${ev.instance_id}:${ev.stage_id}`;
    const current = stageEntryMap.get(key);
    if (!current || new Date(ev.created_at).getTime() < new Date(current).getTime()) {
      stageEntryMap.set(key, ev.created_at);
    }
  });

  // Count reprocesses
  const reprocessMap = new Map<string, number>();
  (events ?? []).forEach((ev: any) => {
    if (ev.event_type !== 'task_reprocessed' || !ev.stage_id) return;
    const key = ev.stage_id;
    reprocessMap.set(key, (reprocessMap.get(key) ?? 0) + 1);
  });

  // Aggregate per stage
  const stageAgg = new Map<string, {
    stageId: string;
    stageName: string;
    stageOrder: number;
    leadTimes: number[];
    processingTimes: number[];
    totalTasks: number;
    slaBreaches: number;
    reprocesses: number;
    slaHours: number | null;
  }>();

  (tasks as any[]).forEach((task) => {
    const stageId = task.stage_id;
    if (!stageId) return;

    const agg = stageAgg.get(stageId) ?? {
      stageId,
      stageName: task.stage?.name ?? task.stage_name ?? stageId,
      stageOrder: task.stage?.stage_order ?? 999,
      leadTimes: [],
      processingTimes: [],
      totalTasks: 0,
      slaBreaches: 0,
      reprocesses: 0,
      slaHours: task.stage?.sla_hours ?? null,
    };

    agg.totalTasks += 1;
    if (task.is_overdue) agg.slaBreaches += 1;

    // Processing time (TP): started → completed
    if (task.started_at && task.completed_at) {
      const tp = (new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()) / 60000;
      if (tp > 0 && Number.isFinite(tp)) agg.processingTimes.push(tp);
    }

    // Lead time: entry → completed
    const entryKey = `${task.instance_id}:${stageId}`;
    const enteredAt = stageEntryMap.get(entryKey) ?? task.created_at ?? task.started_at;
    if (enteredAt && task.completed_at) {
      const lt = (new Date(task.completed_at).getTime() - new Date(enteredAt).getTime()) / 60000;
      if (lt > 0 && Number.isFinite(lt)) agg.leadTimes.push(lt);
    }

    stageAgg.set(stageId, agg);
  });

  // Add reprocess counts
  reprocessMap.forEach((count, stageId) => {
    const agg = stageAgg.get(stageId);
    if (agg) agg.reprocesses = count;
  });

  // Convert to StageMetrics
  return [...stageAgg.values()]
    .map((agg) => {
      const avgLeadTime = agg.leadTimes.length > 0
        ? agg.leadTimes.reduce((s, v) => s + v, 0) / agg.leadTimes.length
        : 0;
      const avgTP = agg.processingTimes.length > 0
        ? agg.processingTimes.reduce((s, v) => s + v, 0) / agg.processingTimes.length
        : 0;

      const standards = stageStandards.get(agg.stageId);
      return {
        stageId: agg.stageId,
        stageName: agg.stageName,
        stageOrder: agg.stageOrder,
        avgLeadTime: Math.round(avgLeadTime),
        avgTP: Math.round(avgTP),
        avgTRA: Math.max(0, Math.round(avgLeadTime - avgTP)),
        slaRate: agg.totalTasks > 0 ? ((agg.totalTasks - agg.slaBreaches) / agg.totalTasks) * 100 : 100,
        totalTasks: agg.totalTasks,
        slaBreach: agg.slaBreaches,
        reprocessCount: agg.reprocesses,
        tpStandard: standards?.tp ?? null,
        traStandard: standards?.tra ?? null,
      };
    })
    .sort((a, b) => a.stageOrder - b.stageOrder);
}

/* ─── Helpers ────────────────────────────────────────────── */

function fmtTime(minutes: number): string {
  if (minutes <= 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function healthColor(slaRate: number): string {
  if (slaRate >= 95) return 'var(--enterprise-primary, #10B981)';
  if (slaRate >= 80) return '#F59E0B';
  return '#EF4444';
}

function healthBg(slaRate: number): string {
  if (slaRate >= 95) return 'rgba(16,185,129,0.08)';
  if (slaRate >= 80) return 'rgba(245,158,11,0.08)';
  return 'rgba(239,68,68,0.08)';
}

function healthLabel(slaRate: number): string {
  if (slaRate >= 95) return 'Saudável';
  if (slaRate >= 80) return 'Atenção';
  return 'Crítico';
}

function deviationLabel(real: number, standard: number | null): { text: string; color: string } | null {
  if (standard === null || standard <= 0) return null;
  const pct = ((real - standard) / standard) * 100;
  if (Math.abs(pct) < 5) return { text: '≈ previsto', color: 'var(--text-secondary)' };
  if (pct > 0) return { text: `+${Math.round(pct)}%`, color: '#EF4444' };
  return { text: `${Math.round(pct)}%`, color: '#10B981' };
}

/* ─── Component ──────────────────────────────────────────── */

export const FlowStageAnalysisPanel: React.FC<FlowStageAnalysisPanelProps> = ({
  organizationId,
  templateId,
  flowName,
}) => {
  const { data: stages, isPending, error } = useQuery({
    queryKey: ['flow-stage-analysis', organizationId, templateId],
    enabled: Boolean(organizationId && templateId),
    queryFn: () => fetchStageMetrics(organizationId, templateId),
    staleTime: 60_000,
  });

  const bottleneckId = useMemo(() => {
    if (!stages?.length) return null;
    const maxLead = Math.max(...stages.map(s => s.avgLeadTime));
    if (maxLead <= 0) return null;
    const worst = stages.reduce((w, s) => s.avgLeadTime > (w?.avgLeadTime ?? 0) ? s : w, stages[0]);
    // Only flag bottleneck if it's meaningfully worse than average
    const avgLead = stages.reduce((sum, s) => sum + s.avgLeadTime, 0) / stages.length;
    if (avgLead <= 0 || worst.avgLeadTime <= avgLead * 1.2) return null;
    return worst.stageId;
  }, [stages]);

  const overallSla = useMemo(() => {
    if (!stages?.length) return 0;
    const total = stages.reduce((s, st) => s + st.totalTasks, 0);
    const breaches = stages.reduce((s, st) => s + st.slaBreach, 0);
    return total > 0 ? ((total - breaches) / total) * 100 : 100;
  }, [stages]);

  if (isPending) {
    return (
      <div className="px-3 py-6 text-center">
        <span className="text-[11px] text-[var(--text-secondary)] animate-pulse">Carregando análise de etapas...</span>
      </div>
    );
  }

  if (error || !stages?.length) {
    return (
      <div className="px-3 py-4 text-center">
        <span className="text-[11px] text-[var(--text-secondary)]">
          Sem dados de etapas para este fluxo
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Summary bar */}
      <div className="flex items-center gap-4 text-[11px]">
        <span className="text-[var(--text-secondary)]">
          {flowName && <span className="font-medium text-[var(--text-primary)]">{flowName}</span>}
          {' · '}{stages.length} etapas
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: healthColor(overallSla) }} />
          SLA geral: <span className="font-semibold text-[var(--text-primary)]">{overallSla.toFixed(1)}%</span>
        </span>
        {bottleneckId && (
          <span className="flex items-center gap-1 text-[#F59E0B]">
            <AlertTriangle size={11} />
            Gargalo: <span className="font-medium">{stages.find(s => s.stageId === bottleneckId)?.stageName}</span>
          </span>
        )}
      </div>

      {/* Pipeline — horizontal stage blocks */}
      <div className="flex gap-0 overflow-x-auto">
        {stages.map((stage, idx) => {
          const isBottleneck = stage.stageId === bottleneckId;
          const color = healthColor(stage.slaRate);
          const bg = healthBg(stage.slaRate);
          const tpRatio = stage.avgLeadTime > 0 ? (stage.avgTP / stage.avgLeadTime) * 100 : 100;

          return (
            <React.Fragment key={stage.stageId}>
              {/* Connector arrow */}
              {idx > 0 && (
                <div className="flex items-center shrink-0 px-0.5">
                  <div className="w-4 h-px bg-[var(--border-default)]" />
                  <div className="w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[5px] border-l-[var(--border-default)]" />
                </div>
              )}

              {/* Stage block */}
              <div
                className="shrink-0 rounded-[6px] border p-2.5 min-w-[160px] max-w-[200px] transition-all"
                style={{
                  borderColor: isBottleneck ? '#F59E0B' : 'var(--border-default)',
                  backgroundColor: bg,
                  boxShadow: isBottleneck ? '0 0 0 1px rgba(245,158,11,0.3)' : 'none',
                }}
              >
                {/* Stage header */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span
                    className="inline-block h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-[11px] font-semibold text-[var(--text-primary)] truncate">
                    {stage.stageName}
                  </span>
                  {isBottleneck && (
                    <AlertTriangle size={11} className="shrink-0 text-[#F59E0B]" />
                  )}
                </div>

                {/* TP / TRA bars */}
                <div className="mb-2">
                  <div className="h-[6px] w-full rounded-full bg-[var(--border-default)] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(tpRatio, 4)}%`,
                        backgroundColor: color,
                      }}
                      title={`TP: ${fmtTime(stage.avgTP)} (${tpRatio.toFixed(0)}%)`}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-[9px] text-[var(--text-secondary)]">
                    <span>TP {fmtTime(stage.avgTP)}{stage.tpStandard !== null && <span className="ml-0.5 opacity-60">/ {fmtTime(stage.tpStandard)}</span>}</span>
                    <span>TRA {fmtTime(stage.avgTRA)}{stage.traStandard !== null && <span className="ml-0.5 opacity-60">/ {fmtTime(stage.traStandard)}</span>}</span>
                  </div>
                </div>

                {/* Deviation indicators */}
                {(stage.tpStandard !== null || stage.traStandard !== null) && (
                  <div className="flex gap-2 mb-1 text-[9px]">
                    {(() => { const d = deviationLabel(stage.avgTP, stage.tpStandard); return d ? <span style={{ color: d.color }}>TP {d.text}</span> : null; })()}
                    {(() => { const d = deviationLabel(stage.avgTRA, stage.traStandard); return d ? <span style={{ color: d.color }}>TRA {d.text}</span> : null; })()}
                  </div>
                )}

                {/* Metrics row */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  <div className="flex items-center gap-1" title="Tempo total na etapa">
                    <Clock size={9} className="text-[var(--text-secondary)]" />
                    <span className="text-[var(--text-secondary)]">Lead:</span>
                    <span className="font-medium text-[var(--text-primary)]">{fmtTime(stage.avgLeadTime)}</span>
                  </div>
                  <div className="flex items-center gap-1" title="SLA compliance">
                    <Zap size={9} style={{ color }} />
                    <span className="text-[var(--text-secondary)]">SLA:</span>
                    <span className="font-medium" style={{ color }}>{stage.slaRate.toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center gap-1" title="Tarefas concluídas">
                    <Timer size={9} className="text-[var(--text-secondary)]" />
                    <span className="text-[var(--text-secondary)]">Tasks:</span>
                    <span className="font-medium text-[var(--text-primary)]">{stage.totalTasks}</span>
                  </div>
                  {stage.reprocessCount > 0 && (
                    <div className="flex items-center gap-1 text-[#F59E0B]" title="Reprocessamentos">
                      <AlertTriangle size={9} />
                      <span>{stage.reprocessCount} reproc.</span>
                    </div>
                  )}
                </div>

                {/* Health label */}
                <div className="mt-2 text-center">
                  <span
                    className="inline-block rounded-sm px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                    style={{ backgroundColor: bg, color, border: `1px solid ${color}33` }}
                  >
                    {healthLabel(stage.slaRate)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Detailed table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-[11px]">
          <thead className="border-b border-[var(--border-default)] bg-[var(--bg-surface)] text-[10px] uppercase tracking-[0.08em] text-[var(--text-secondary)]">
            <tr>
              <th className="px-2.5 py-1 text-left">Etapa</th>
              <th className="px-2.5 py-1 text-right">Lead Time</th>
              <th className="px-2.5 py-1 text-right">TP (Processamento)</th>
              <th className="px-2.5 py-1 text-right">TP Previsto</th>
              <th className="px-2.5 py-1 text-right">TRA (Espera)</th>
              <th className="px-2.5 py-1 text-right">TRA Previsto</th>
              <th className="px-2.5 py-1 text-right">SLA %</th>
              <th className="px-2.5 py-1 text-right">Tarefas</th>
              <th className="px-2.5 py-1 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => {
              const isBottleneck = stage.stageId === bottleneckId;
              const color = healthColor(stage.slaRate);

              const tpDev = deviationLabel(stage.avgTP, stage.tpStandard);
              const traDev = deviationLabel(stage.avgTRA, stage.traStandard);

              return (
                <tr
                  key={stage.stageId}
                  className={`border-b border-[var(--border-default)] ${isBottleneck ? 'bg-[rgba(245,158,11,0.05)]' : ''}`}
                >
                  <td className="px-2.5 py-1.5 text-[var(--text-primary)] font-medium">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                      {stage.stageName}
                      {isBottleneck && <AlertTriangle size={10} className="text-[#F59E0B]" />}
                    </div>
                  </td>
                  <td className="px-2.5 py-1.5 text-right text-[var(--text-primary)] font-mono tabular-nums">
                    {fmtTime(stage.avgLeadTime)}
                  </td>
                  <td className="px-2.5 py-1.5 text-right text-[var(--text-primary)] font-mono tabular-nums">
                    {fmtTime(stage.avgTP)}
                    {tpDev && <span className="ml-1 text-[9px] font-semibold" style={{ color: tpDev.color }}>{tpDev.text}</span>}
                  </td>
                  <td className="px-2.5 py-1.5 text-right font-mono tabular-nums text-[var(--text-secondary)]">
                    {stage.tpStandard !== null ? fmtTime(stage.tpStandard) : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right text-[var(--text-primary)] font-mono tabular-nums">
                    {fmtTime(stage.avgTRA)}
                    {traDev && <span className="ml-1 text-[9px] font-semibold" style={{ color: traDev.color }}>{traDev.text}</span>}
                  </td>
                  <td className="px-2.5 py-1.5 text-right font-mono tabular-nums text-[var(--text-secondary)]">
                    {stage.traStandard !== null ? fmtTime(stage.traStandard) : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-right font-semibold" style={{ color }}>
                    {stage.slaRate.toFixed(1)}%
                  </td>
                  <td className="px-2.5 py-1.5 text-right text-[var(--text-primary)] tabular-nums">
                    {stage.totalTasks}
                    {stage.slaBreach > 0 && (
                      <span className="ml-1 text-[#EF4444]">({stage.slaBreach} atraso)</span>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span
                      className="inline-block rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                      style={{ backgroundColor: healthBg(stage.slaRate), color }}
                    >
                      {healthLabel(stage.slaRate)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
